import { useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  InlineError,
  Panel,
  Skeleton,
  Textarea,
} from '../../components/ui';
import { blockDateLabel, blocksByGroup, durationLabel, longDate } from './leaveLens';
import {
  leaveErrorMessage,
  useApprovalQueue,
  useCancellationQueue,
  useDecideCancellation,
  useDecideLeave,
  type ApprovalQueueRow,
  type DecisionConflict,
} from './queries';

/**
 * The approver queue, as Code.gs renders it.
 *
 * Two sections in this order: Pending Leave, grouped into consecutive blocks
 * with a checkbox per day, then Cancellation Requests one by one. Empty, it
 * says "Nothing is waiting for approval." Those are the Apps Script's words
 * and its order, kept on purpose.
 *
 * The decision dialog is the Apps Script's too: a note, then Confirm Approval
 * or Confirm Rejection. If approving would overlap somebody already off, the
 * first confirm comes back with the clash listed and decides nothing; the
 * second goes through.
 */
export function ApprovalQueue() {
  const pending = useApprovalQueue(true);
  const cancels = useCancellationQueue(true);

  if (pending.isLoading || cancels.isLoading) return <Skeleton />;
  if (pending.isError) return <InlineError>{leaveErrorMessage(String(pending.error))}</InlineError>;
  if (cancels.isError) return <InlineError>{leaveErrorMessage(String(cancels.error))}</InlineError>;

  const rows = pending.data ?? [];
  const cancellations = cancels.data ?? [];

  if (rows.length === 0 && cancellations.length === 0) {
    return <EmptyState title="Nothing is waiting for approval" />;
  }

  return (
    <>
      {rows.length > 0 ? (
        <Panel title="Pending Leave">
          <div className="leave-blocks">
            {blocksByGroup(rows).map((block) => (
              <ApprovalBlock key={block[0]?.id ?? ''} block={block} />
            ))}
          </div>
        </Panel>
      ) : null}

      {cancellations.length > 0 ? (
        <Panel title="Cancellation Requests">
          <ul className="leave-rows">
            {cancellations.map((row) => (
              <CancellationRow key={row.id} row={row} />
            ))}
          </ul>
        </Panel>
      ) : null}
    </>
  );
}

function ApprovalBlock({ block }: { block: ApprovalQueueRow[] }) {
  // Every day starts ticked, as the Apps Script does. Untick a day to leave it
  // pending while the rest of the run is decided.
  const [checked, setChecked] = useState<string[]>(block.map((row) => row.id));
  const [pending, setPending] = useState<{ approve: boolean } | null>(null);
  const first = block[0];
  if (first === undefined) return null;

  return (
    <div className="leave-block">
      <div className="leave-block-head">
        <div>
          <strong>{first.employee_name}</strong>
          <span className="leave-row-meta">
            {blockDateLabel(block.map((row) => row.leave_date))}
          </span>
        </div>
        <span className="leave-row-meta">
          {first.leave_type} &middot; {durationLabel(first)}
          {block.length > 1 ? ` · ${String(block.length)} days` : ''}
        </span>
      </div>

      <ul className="leave-block-days">
        {block.map((row) => (
          <li key={row.id}>
            <label className="leave-day-check">
              <input
                type="checkbox"
                checked={checked.includes(row.id)}
                onChange={() => {
                  setChecked(
                    checked.includes(row.id)
                      ? checked.filter((id) => id !== row.id)
                      : [...checked, row.id],
                  );
                }}
              />
              <span>{longDate(row.leave_date)}</span>
              {row.edit_count > 0 ? (
                <Badge tone="neutral" micro>
                  edited
                </Badge>
              ) : null}
            </label>
          </li>
        ))}
      </ul>

      <div className="leave-row-actions">
        <Button
          disabled={checked.length === 0}
          onClick={() => {
            setPending({ approve: true });
          }}
        >
          Approve
        </Button>
        <Button
          variant="ghost"
          disabled={checked.length === 0}
          onClick={() => {
            setPending({ approve: false });
          }}
        >
          Reject
        </Button>
      </div>

      {pending === null ? null : (
        <DecisionDialog
          ids={checked}
          approve={pending.approve}
          onClose={() => {
            setPending(null);
          }}
        />
      )}
    </div>
  );
}

function DecisionDialog({
  ids,
  approve,
  onClose,
}: {
  ids: string[];
  approve: boolean;
  onClose: () => void;
}) {
  const decide = useDecideLeave();
  const [note, setNote] = useState('');
  const [conflicts, setConflicts] = useState<DecisionConflict[] | null>(null);
  const [error, setError] = useState('');

  const send = (override: boolean) => {
    setError('');
    decide.mutate(
      { ids, approve, note, override },
      {
        onSuccess: (result) => {
          if (result.ok) {
            onClose();
            return;
          }
          // Nothing was decided. Show who the clash is with and let the
          // approver send it again knowing.
          setConflicts(result.conflicts);
        },
        onError: (err: Error) => {
          setError(leaveErrorMessage(err.message));
        },
      },
    );
  };

  return (
    <Dialog
      title={approve ? 'Approve Leave Request' : 'Reject Leave Request'}
      onClose={onClose}
      open
    >
      {error === '' ? null : <InlineError>{error}</InlineError>}

      {conflicts === null ? null : (
        <div className="leave-conflicts">
          <p className="leave-conflict-lede">Overlapping approved leave was found.</p>
          <ul>
            {conflicts.map((clash) => (
              <li key={`${clash.candidate_id}-${clash.other_id}`}>
                {longDate(clash.leave_date)}: {clash.candidate_name} would overlap{' '}
                {clash.other_name}.
              </li>
            ))}
          </ul>
        </div>
      )}

      <Textarea
        label="Note"
        rows={3}
        maxLength={500}
        value={note}
        onChange={(event) => {
          setNote(event.target.value);
        }}
      />

      <div className="leave-row-actions">
        <Button
          disabled={decide.isPending}
          onClick={() => {
            send(conflicts !== null);
          }}
        >
          {conflicts !== null
            ? 'Approve anyway'
            : approve
              ? 'Confirm Approval'
              : 'Confirm Rejection'}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}

function CancellationRow({
  row,
}: {
  row: {
    id: string;
    employee_name: string;
    leave_date: string;
    leave_type: string;
    duration_type: string;
    start_time: string | null;
    end_time: string | null;
  };
}) {
  const decide = useDecideCancellation();
  const [error, setError] = useState('');

  const send = (approve: boolean) => {
    setError('');
    decide.mutate(
      { requestId: row.id, approve, note: '' },
      {
        onError: (err: Error) => {
          setError(leaveErrorMessage(err.message));
        },
      },
    );
  };

  return (
    <li className="leave-row">
      <div className="leave-row-main">
        <div className="leave-row-when">
          <span className="leave-row-date">
            {row.employee_name} &middot; {longDate(row.leave_date)}
          </span>
          <span className="leave-row-meta">
            {row.leave_type} &middot; {durationLabel(row)}
          </span>
        </div>
        <Badge tone="review">Withdrawal requested</Badge>
      </div>
      {error === '' ? null : <InlineError>{error}</InlineError>}
      <div className="leave-row-actions">
        <Button
          disabled={decide.isPending}
          onClick={() => {
            send(true);
          }}
        >
          Approve Cancellation
        </Button>
        <Button
          variant="ghost"
          disabled={decide.isPending}
          onClick={() => {
            send(false);
          }}
        >
          Reject Cancellation
        </Button>
      </div>
    </li>
  );
}
